import { Image as ExpoImage } from "expo-image";
import { type ComponentProps, useCallback, useState } from "react";
import { Image as RNImage } from "react-native";
import { type GetProps, styled } from "tamagui";

const StyledImage = styled(ExpoImage, { name: "Image" });

type ExpoImageProps = ComponentProps<typeof ExpoImage>;

export type ImageProps = Omit<GetProps<typeof StyledImage>, "objectFit"> & {
  objectFit?: ExpoImageProps["contentFit"];
  objectPosition?: ExpoImageProps["contentPosition"];
};

/**
 * Metro embeds the intrinsic dimensions for both native and web, so we can size the images up front.
 */
function staticAspectRatio(source: ImageProps["source"]): number | undefined {
  if (source == null || typeof source === "string" || Array.isArray(source)) {
    return undefined;
  }
  try {
    const resolved = RNImage.resolveAssetSource(source as never);
    if (resolved?.width && resolved?.height) {
      return resolved.width / resolved.height;
    }
  } catch {
    // In case resolveAssetSource throws, fall back to none.
  }
  return undefined;
}

/** App `Image`, backed by **expo-image** and wrapped with Tamagui's `styled()`*/
const Image = ({
  objectFit = "cover",
  objectPosition,
  ...props
}: ImageProps) => {
  const aspectRatio =
    props.aspectRatio ??
    (props.height == null ? staticAspectRatio(props.source) : undefined);

  return (
    <StyledImage
      contentFit={objectFit}
      {...(objectPosition ? { contentPosition: objectPosition } : {})}
      {...props}
      {...(aspectRatio != null ? { aspectRatio } : {})}
    />
  );
};

export default Image;

/** Helper for images with only a width and no height */
export function useImageAspectRatio() {
  const [aspectRatio, setAspectRatio] = useState<number>();
  const onLoad = useCallback(
    (e: { source?: { width?: number; height?: number } | null }) => {
      const { width, height } = e.source ?? {};
      if (width && height) setAspectRatio(width / height);
    },
    [],
  );
  return { aspectRatio, onLoad };
}
